import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Constants } from '../../core/constants';
import { LocalCacheManager } from '../managers/local-cache.manager';

enum HttpMethod {
    Get,
    Post,
    Put,
    Patch,
    Delete,
}

@Injectable()
export class ApiService {
    protected HttpMethod = HttpMethod;
    constructor(protected http: HttpClient,
        protected localCacheManager: LocalCacheManager) {
    }

    protected apiRequest<T>(url: string, httpMethod: HttpMethod, jsonInput?: any): Observable<T> {
        return this.getUrlRequestWithAuthorizationHeader<T>(url, httpMethod, jsonInput);
    }

    protected apiRequestWithBasicAuth<T>(url: string, httpMethod: HttpMethod, email, password): Observable<T> {
        let headers;

        // Don't assign 'Content-Type - application/json' header for image upload endpoint
        if (!url.toLowerCase().includes('imageassets/uploadimage') && !url.includes('irrigation/import')) {
            headers = new HttpHeaders().set('Content-Type', 'application/json').append('Authorization', 'Basic ?email=' + email + '&password=' + password);
        }
        else {
            headers = new HttpHeaders().set('Authorization', 'Basic ?email=' + email + '&password=' + password);
        }

        return this.getRequest<T>(url, httpMethod, headers);
    }

    protected apiRequestWithoutAuthorization<T>(url: string, httpMethod: HttpMethod) {
        return this.getRequest(url, httpMethod);
    }

    protected apiRequestToGetBlob<T>(url: string, httpMethod: HttpMethod, jsonInput?: string): Observable<Blob> {
        return this.getUrlRequestWithAuthorizationAndAcceptType(url, httpMethod, 'application/blob', jsonInput);
    }

    public apiRequestToGetBlobAsFile<T>(url: string): Observable<T> {
        const headers = new HttpHeaders().set('Content-Type', 'application/json').append('Accept', 'image/jpeg');
        return this.http.get<T>(url, { headers: headers, responseType: 'blob' as 'json' });
    }

    protected apiRequestToGetLocationWeatherForecast<T>(url: string, httpMethod: HttpMethod)
    {
        const headers = new HttpHeaders().set('Content-Type', 'text/plain').append('Accept', 'application/json').append('x-api-key', Constants.WeatherApiKey);

        return this.getRequest(url, httpMethod, headers);
    }

    private getUrlRequestWithAuthorizationHeader<T>(url: string, httpMethod: HttpMethod, httpBody?: any): Observable<T> | any {
        const userId = this.localCacheManager.userId;
        const accessToken = this.localCacheManager.accessToken;

        let headers;

        // Don't assign 'Content-Type - application/json' header for image upload endpoints
        if (!url.toLowerCase().includes('imageassets/uploadimage')
        && !url.includes('irrigation/import')
        && !url.includes('firmware/upload')
        && !url.toLowerCase().includes('device/export'))
        {
            headers = new HttpHeaders().set('Content-Type', 'application/json').append('Accept', 'application/json');
        } else if (url.includes('/export')) {
            if (userId && accessToken) {
                headers = new HttpHeaders().set('Authorization', 'Basic ?id=' + userId + '&token=' + accessToken);
            }
            return this.apiRequestToGetTextAsFile(url, headers, httpBody);
        } else {
            headers = new HttpHeaders().set('Accept', 'application/json');
        }

        if (userId && accessToken) {
            headers = headers.append('Authorization', 'Basic ?id=' + userId + '&token=' + accessToken);
        }

        return this.getRequest<T>(url, httpMethod, headers, httpBody);
    }


    public apiRequestToGetTextAsFile(url: string, headers: HttpHeaders, httpBody: string): Observable<string> | any {
        headers = headers.set('Content-Type', 'text/csv').append('Accept', 'text/csv');
        return this.http.post(url, httpBody, { headers: headers, responseType: 'text' });
    }

    getUrlRequestWithAuthorizationAndAcceptType<T>(url: string, httpMethod: HttpMethod, acceptType: string, httpBody?: string): Observable<T> {

        const userId = this.localCacheManager.userId;
        const accessToken = this.localCacheManager.accessToken;

        let headers = new HttpHeaders().set('Content-Type', 'application/json').append('Accept', acceptType);
        if (userId && accessToken) {
            headers = headers.append('Authorization', 'Basic ?id=' + userId + '&token=' + accessToken);
        }
        return this.http.get<T>(url, { headers: headers, responseType: 'blob' as 'json' });
    }

    protected apiGetRequestForBlob<T>(url: string): Observable<T> {
        const userId = this.localCacheManager.userId;
        const accessToken = this.localCacheManager.accessToken;

        let headers = new HttpHeaders().set('Content-Type', 'application/json').append('Accept', 'image/jpeg');
        if (userId && accessToken) {
            headers = headers.append('Authorization', 'Basic ?id=' + userId + '&token=' + accessToken);
        }
        return this.http.get<T>(url, { headers: headers, responseType: 'blob' as 'json' });
    }

    private getRequest<T>(url: string, httpMethod: HttpMethod, headers?: HttpHeaders, httpBody?: string): Observable<T> {
        switch (httpMethod) {
            case HttpMethod.Get:
                return this.http.get<T>(url, { headers: headers });
            case HttpMethod.Post:
                return this.http.post<T>(url, httpBody, { headers: headers });
            case HttpMethod.Put:
                return this.http.put<T>(url, httpBody, { headers: headers });
            case HttpMethod.Patch:
                return this.http.patch<T>(url, httpBody, { headers: headers });
            case HttpMethod.Delete:
                return this.http.delete<T>(url, { headers: headers });
            default:
                throw new Error('Support for selected HttpMethod Not Implemented.');
        }
    }

}
